/**
 * Number formatting for story charts and KPIs: one settings shape, three outputs.
 *
 * - `toD3Format`: a d3-format specifier, when the settings fit one (no compact, no non-"$" symbol).
 * - `encodeNumberFormat` / `formatEncoded`: a compact string the Vega `formatType` hook decodes, for
 *   everything d3 cannot say (K/M/B suffixes, other currency symbols, "compact only when large").
 * - `formatNumber`: the same result for HTML text (KPI number and delta).
 *
 * Nothing here names a tenant currency: a symbol comes from the user's pick or from the measure's model.
 */
export const CURRENCY_SYMBOLS = ["$", "€", "£", "₹", "¥"] as const;
export type CurrencySymbol = (typeof CURRENCY_SYMBOLS)[number];

/** What the user set. Every field is optional: unset means "the default". */
export interface NumberFormatSettings {
  /** Fixed decimals, 0 to 4. Unset: up to 2, trailing zeros dropped. */
  decimals?: 0 | 1 | 2 | 3 | 4;
  /** Thousands separators. Unset: on. */
  thousands?: boolean;
  /** A symbol from the list, or "none". Unset: the model's. */
  currency?: CurrencySymbol | "none";
  /** Multiply by 100 and add "%". Unset: the model's. */
  percent?: boolean;
  /** `true` always, `false` never. Unset: on axes only, and only for large values. */
  compact?: boolean;
}

/** What the measure's model says about its unit; the default when the user set nothing. */
export interface ModelNumberFormat {
  kind?: "currency" | "percent";
  /** The currency's symbol; no symbol is drawn when absent. */
  symbol?: string;
}

export type NumberFormatContext = "axis" | "value";

export const NUMBER_FORMAT_TYPE = "magicbiNumber";
export const DECIMALS_RANGE = { min: 0, max: 4 } as const;
const AUTO_COMPACT_FROM = 10_000;
const DEFAULT_MAX_DECIMALS = 2;

interface Resolved {
  decimals: number;
  fixed: boolean;
  thousands: boolean;
  symbol: string;
  percent: boolean;
  compact: "auto" | "always" | "never";
}

const clampDecimals = (n: number): number =>
  Math.min(DECIMALS_RANGE.max, Math.max(DECIMALS_RANGE.min, Math.round(n)));

const resolve = (settings: NumberFormatSettings = {}, model: ModelNumberFormat = {}): Resolved => {
  const symbol =
    settings.currency !== undefined
      ? settings.currency === "none"
        ? ""
        : settings.currency
      : model.kind === "currency"
        ? (model.symbol ?? "")
        : "";
  const percent = settings.percent ?? (settings.currency === undefined && model.kind === "percent");
  return {
    decimals: clampDecimals(settings.decimals ?? DEFAULT_MAX_DECIMALS),
    fixed: settings.decimals !== undefined,
    thousands: settings.thousands ?? true,
    symbol: percent ? "" : symbol,
    percent,
    compact: settings.compact === undefined ? "auto" : settings.compact ? "always" : "never",
  };
};

/** True when the settings and model leave every default in place. */
export const isDefaultNumberFormat = (settings?: NumberFormatSettings): boolean =>
  !settings || Object.values(settings).every((value) => value === undefined);

/**
 * A d3-format specifier for the settings, or `undefined` when d3 cannot express them (compact, a
 * symbol other than "$"). In the "value" context an unset compact means never, so the default
 * (thousands, up to 2 decimals) is `",.2~f"`. On an axis an unset compact means "when large", which
 * d3 cannot say, so that case is `undefined` too.
 */
export const toD3Format = (
  settings?: NumberFormatSettings,
  model?: ModelNumberFormat,
  context: NumberFormatContext = "value",
): string | undefined => {
  const r = resolve(settings, model);
  if (r.compact === "always" || (r.compact === "auto" && context === "axis")) return undefined;
  if (r.symbol !== "" && r.symbol !== "$") return undefined;
  const type = r.percent ? "%" : "f";
  const trim = r.fixed ? "" : "~";
  return `${r.symbol}${r.thousands ? "," : ""}.${r.decimals}${trim}${type}`;
};

const SUFFIXES = ["", "K", "M", "B", "T"];

const group = (digits: string, thousands: boolean): string =>
  thousands ? digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : digits;

const fixedOrTrimmed = (value: number, r: Resolved, maxDecimals: number): string => {
  const text = value.toFixed(maxDecimals);
  const trimmed = r.fixed || !text.includes(".") ? text : text.replace(/\.?0+$/, "");
  const [whole, fraction] = trimmed.split(".");
  return `${group(whole, r.thousands)}${fraction ? `.${fraction}` : ""}`;
};

const formatResolved = (value: number, r: Resolved, context: NumberFormatContext): string => {
  if (!Number.isFinite(value)) return "";
  const scaled = r.percent ? value * 100 : value;
  const abs = Math.abs(scaled);
  const compact = r.compact === "always" || (r.compact === "auto" && context === "axis" && abs >= AUTO_COMPACT_FROM);
  let body: string;
  if (compact && abs >= 1000) {
    let tier = Math.min(SUFFIXES.length - 1, Math.floor(Math.log10(abs) / 3));
    let shown = abs / 1000 ** tier;
    // Compact reads best with one decimal unless the user set a count.
    const places = r.fixed ? r.decimals : Math.min(r.decimals, 1);
    // 999.96K rounds to 1000K: promote to the next tier.
    if (Number(shown.toFixed(places)) >= 1000 && tier < SUFFIXES.length - 1) {
      tier += 1;
      shown = abs / 1000 ** tier;
    }
    body = `${fixedOrTrimmed(shown, r, places)}${SUFFIXES[tier]}`;
  } else {
    body = fixedOrTrimmed(abs, r, r.decimals);
  }
  const negative = scaled < 0 && Number(body.replace(/[^\d.]/g, "")) !== 0;
  return `${negative ? "-" : ""}${r.symbol}${body}${r.percent ? "%" : ""}`;
};

/** The number as text, for the KPI tile and any HTML label. */
export const formatNumber = (
  value: number,
  settings?: NumberFormatSettings,
  model?: ModelNumberFormat,
  context: NumberFormatContext = "value",
): string => formatResolved(value, resolve(settings, model), context);

/** The string a spec carries in `format`; `formatEncoded` is its other half. */
export const encodeNumberFormat = (
  settings?: NumberFormatSettings,
  model?: ModelNumberFormat,
  context: NumberFormatContext = "value",
): string => {
  const r = resolve(settings, model);
  return JSON.stringify({ ...r, context });
};

/** What the Vega `formatType` function runs: decodes `encodeNumberFormat`'s string and formats. */
export const formatEncoded = (value: unknown, encoded: string): string => {
  const n = typeof value === "number" ? value : Number(value);
  if (value === null || value === undefined || Number.isNaN(n)) return value == null ? "" : String(value);
  try {
    const { context, ...r } = JSON.parse(encoded) as Resolved & { context: NumberFormatContext };
    return formatResolved(n, r, context);
  } catch {
    return String(value);
  }
};

/**
 * The settings an old Format-tab d3 specifier stands for, where obvious: the Currency and Percentage
 * options ("$~s", "$,.2f", ".1%"). Anything else is `undefined`: the default stays.
 */
export const settingsFromLegacyFormat = (format: string | undefined): NumberFormatSettings | undefined => {
  const match = /^(\$?)(,?)\.?(\d?)(~?)([sf%])$/.exec(format ?? "");
  if (!match) return undefined;
  const [, dollar, comma, digits, , type] = match;
  const settings: NumberFormatSettings = {};
  if (type === "%") settings.percent = true;
  else if (dollar) settings.currency = "$";
  if (type === "s") settings.compact = true;
  if (type !== "s") settings.thousands = comma === ",";
  if (digits !== "" && type !== "s") settings.decimals = clampDecimals(Number(digits)) as NumberFormatSettings["decimals"];
  return settings;
};
